/**
 * AvatarPicker — Subida y preview de foto de perfil.
 *
 * Modos:
 *   - own (default): sube vía /api/equipo/me/foto
 *   - userId="USER-XXX": admin sube para otro user (CEO/COO/CFO)
 *
 * Props:
 *   - currentUrl?: string  — URL actual
 *   - nombre?: string      — para generar iniciales como fallback
 *   - userId?: string      — si se pasa, modo admin
 *   - size?: number        — tamaño en px (default 120)
 *   - onUploaded?: (newUrl) => void
 */
import { useState, useRef, useEffect } from "react";
import { Camera, Loader2, Trash2, AlertCircle, Image as ImageIcon, X } from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { cn } from "../lib/utils.js";
import { useConfirm } from "./ConfirmDialog.jsx";
import { invalidarFotos } from "./UserAvatar.jsx";

// Renueva el avatar en toda la app (cabecera, chats, listas) apenas cambia la foto.
function avisarFotoCambiada() {
  invalidarFotos();
  try { window.dispatchEvent(new Event("crew-foto-actualizada")); } catch { /* sin window */ }
}

function iniciales(nombre) {
  if (!nombre) return "?";
  return nombre.split(" ").filter(Boolean).slice(0, 2).map(s => s.charAt(0).toUpperCase()).join("");
}

/**
 * v6.7.8 · Redimensiona cualquier imagen (HEIC, PNG 20MB, etc.) a un cuadrado JPEG.
 * Recorta al centro (centered crop) para que quede cuadrado.
 * Devuelve un Blob JPEG listo para subir.
 */
async function redimensionarImagen(file, maxSize = 512, quality = 0.85) {
  // Cargar como ImageBitmap (más rápido y soporta HEIC en navegadores modernos)
  const bitmap = await loadBitmap(file);
  const { width, height } = bitmap;

  // Crop cuadrado centrado
  const cropSize = Math.min(width, height);
  const sx = (width  - cropSize) / 2;
  const sy = (height - cropSize) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = maxSize;
  canvas.height = maxSize;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, sx, sy, cropSize, cropSize, 0, 0, maxSize, maxSize);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error("No se pudo procesar la imagen")),
      "image/jpeg",
      quality
    );
  });
}

/* v6.7.9 · Menú modal con 2 opciones */
function AvatarMenu({ onSelfieDesktop, onSelfieMobile, onGaleria, onCancel }) {
  const esMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60" onClick={onCancel}>
      <div className="bg-card rounded-2xl shadow-2xl p-6 min-w-[280px] max-w-sm w-[90vw] space-y-3"
           onClick={e => e.stopPropagation()}>
        <div className="text-sm font-bold mb-2">Foto de perfil</div>
        <button onClick={esMobile ? onSelfieMobile : onSelfieDesktop}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#F5F3FF] hover:bg-[#EDE9FE] text-[#5B21B6] border border-[#DDD6FE] transition">
          <Camera className="h-5 w-5" />
          <span className="font-semibold">Tomar selfie</span>
        </button>
        <button onClick={onGaleria}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#E0F7FB] hover:bg-[#CFFAFE] text-[#155E75] border border-[#A5F3FC] transition">
          <ImageIcon className="h-5 w-5" />
          <span className="font-semibold">Elegir de galería</span>
        </button>
        <button onClick={onCancel}
          className="w-full text-xs text-muted-foreground hover:text-foreground py-1 mt-2">
          Cancelar
        </button>
      </div>
    </div>
  );
}

/* v6.7.9 · Cámara webcam para desktop (getUserMedia) */
function SelfieDesktop({ onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    let s = null;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 720, height: 720 } })
      .then(media => {
        s = media; setStream(media);
        if (videoRef.current) {
          videoRef.current.srcObject = media;
          videoRef.current.play().catch(() => {});
        }
      })
      .catch(e => setError("No se pudo acceder a la cámara: " + e.message));
    return () => { if (s) s.getTracks().forEach(t => t.stop()); };
  }, []);

  function tomar() {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    // Crop cuadrado del centro
    const w = v.videoWidth, h = v.videoHeight;
    const size = Math.min(w, h);
    const sx = (w - size) / 2, sy = (h - size) / 2;
    c.width = 512; c.height = 512;
    const ctx = c.getContext("2d");
    // Mirror horizontal para que se vea como espejo (más natural)
    ctx.translate(512, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, sx, sy, size, size, 0, 0, 512, 512);
    c.toBlob(blob => blob && onCapture(blob), "image/jpeg", 0.85);
  }

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/80">
      <div className="bg-card rounded-2xl shadow-2xl p-4 max-w-md w-[90vw] space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold">Tomar selfie</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4"/></button>
        </div>
        {error ? (
          <div className="text-sm text-[#DC2626] p-4 bg-[#FEF2F2] rounded-lg">{error}</div>
        ) : (
          <>
            <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
              <video ref={videoRef} autoPlay playsInline muted
                className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }}/>
            </div>
            <canvas ref={canvasRef} className="hidden"/>
            <div className="flex gap-2">
              <button onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg border border-border text-sm hover:bg-muted/40">
                Cancelar
              </button>
              <button onClick={tomar} disabled={!stream}
                className="flex-1 px-4 py-2 rounded-lg bg-[#7C3AED] hover:bg-[#5B21B6] text-white text-sm font-semibold disabled:opacity-50">
                <span aria-hidden="true" className="msr mr-1 align-[-0.18em] text-[1.15em]">photo_camera</span>Capturar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

async function loadBitmap(file) {
  // 1. Intento rápido con createImageBitmap (soporta más formatos en navegadores modernos)
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file); } catch (_) {}
  }
  // 2. Fallback: HTMLImageElement
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error("Formato no soportado. Probá con JPG o PNG."));
    img.src = URL.createObjectURL(file);
  });
}

export default function AvatarPicker({ currentUrl, nombre, userId, size = 120, onUploaded, editable = true }) {
  const confirm = useConfirm();
  const [url, setUrl]         = useState(currentUrl || null);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileGaleriaRef = useRef(null);
  const fileSelfieRef  = useRef(null);

  async function handleFile(file) {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      // v6.7.8 · Redimensionar SIEMPRE antes de subir (resuelve foto del celu de 10MB,
      // HEIC del iPhone, PNG gigante, etc.) → JPEG cuadrado max 512px ~80KB.
      const optimizedBlob = await redimensionarImagen(file, 512, 0.85);
      const optimizedFile = new File([optimizedBlob], "avatar.jpg", { type: "image/jpeg" });
      const r = userId ? await api.uploadAvatarFor(userId, optimizedFile) : await api.uploadMyAvatar(optimizedFile);
      setUrl(r.foto_url + "&t=" + Date.now());  // cache-bust
      onUploaded?.(r.foto_url);
      avisarFotoCambiada();
    } catch (e) {
      setError(e.message || "No se pudo subir la foto. Probá con otra imagen.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (userId) return;  // admin delete no implementado, solo own
    const ok = await confirm({ title: "¿Eliminar tu foto de perfil?", variant: "destructive", confirmLabel: "Eliminar" });
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteMyAvatar();
      setUrl(null);
      onUploaded?.(null);
      avisarFotoCambiada();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="inline-flex flex-col items-center gap-2">
      <div
        className={cn(
          "relative rounded-full overflow-hidden border-2 border-border  bg-[#22D3EE] ",
          editable && "cursor-pointer hover:opacity-90 transition"
        )}
        style={{ width: size, height: size }}
        onClick={() => editable && !busy && setMenuOpen(true)}
      >
        {url ? (
          <img src={url} alt={nombre || "avatar"} className="w-full h-full object-cover"
               onError={() => setUrl(null)} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white font-bold"
               style={{ fontSize: size * 0.35 }}>
            {iniciales(nombre)}
          </div>
        )}
        {editable && !busy && (
          <div className="absolute bottom-0 right-0 bg-foreground text-background rounded-full p-2 shadow-lg">
            <Camera className="h-3.5 w-3.5" />
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}
      </div>

      {/* Inputs hidden — uno para galería, otro para cámara nativa móvil */}
      <input
        ref={fileGaleriaRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => { setMenuOpen(false); handleFile(e.target.files?.[0]); e.target.value = ""; }}
      />
      <input
        ref={fileSelfieRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={e => { setMenuOpen(false); handleFile(e.target.files?.[0]); e.target.value = ""; }}
      />

      {/* Menú modal */}
      {menuOpen && !cameraOpen && (
        <AvatarMenu
          onSelfieDesktop={() => { setMenuOpen(false); setCameraOpen(true); }}
          onSelfieMobile={() => fileSelfieRef.current?.click()}
          onGaleria={() => fileGaleriaRef.current?.click()}
          onCancel={() => setMenuOpen(false)}
        />
      )}

      {/* Captura webcam desktop */}
      {cameraOpen && (
        <SelfieDesktop
          onClose={() => setCameraOpen(false)}
          onCapture={blob => {
            setCameraOpen(false);
            const f = new File([blob], "selfie.jpg", { type: "image/jpeg" });
            handleFile(f);
          }}
        />
      )}

      {editable && url && !userId && (
        <button onClick={handleDelete}
                className="text-xs text-[#DC2626] hover:underline flex items-center gap-1">
          <Trash2 className="h-3 w-3" /> Eliminar
        </button>
      )}
      {error && (
        <div className="text-xs text-[#DC2626] flex items-center gap-1">
          <AlertCircle className="h-3 w-3" /> {error}
        </div>
      )}
    </div>
  );
}
